// campaign-transport-update.ts — handler for the 'campaign_transport_update' frame.
//
// PHP publisher: options/tools/campaigns/push_campaign_transport_inc.php broadcasts the GM's bare
// campaign-time transport flags {running, fast_forward} to the campaign channel on discrete GM
// control actions (play / pause / fast-forward) plus a 6s heartbeat while running — NOT the time
// values themselves (those ride campaign_update). Display-only: no DB touch, no version.
//
// Two early exits, mirroring turn-timer-update.ts:
//   • isGM viewer — the GM browser IS the authority (its own buttons drive the local UI and fired
//     this broadcast); consuming its own echo would be pointless. Skip.
//   • non-current campaign — a client can briefly hold a subscription to another campaign; drop
//     frames whose campaign_id doesn't match the campaign the player is currently viewing.

import type { WSMessage } from './types';

type WSCampaignTransportUpdateMessage = Extract<WSMessage, { type: 'campaign_transport_update' }>;

function handleCampaignTransportUpdate(msg: WSCampaignTransportUpdateMessage): void {
    const campaignId = msg?.campaign_id || '';
    if (!campaignId) {
        ftDebugLog('campaign', { warn: 'campaign_transport_update: no campaign_id', msg });
        return;
    }

    // GM is the source of truth — its own UI already reflects play/pause/fast-forward.
    if (window.FateTide?.user?.isGM) {
        return;
    }

    // Only honor the campaign the player is currently viewing.
    if (campaignId !== window.FateTide?.campaign?.id) {
        ftDebugLog('campaign', { phase: 'campaign_transport_update-other-campaign-drop', msgCampaign: campaignId });
        return;
    }

    const running = msg?.running ? 1 : 0;
    const fast_forward = msg?.fast_forward ? 1 : 0;

    // Stash the transport state so the indicator renderer (encounter-summary bundle) and any later
    // summary re-render read a consistent value.
    const ft = window.FateTide ??= {};
    ft.campaignTransport ??= {};
    ft.campaignTransport[campaignId] = { running, fast_forward };

    // Repaint the side-log calendar's transport indicator in place (no DB re-fetch).
    const render = window.ftRenderCampaignTransportIndicator;
    if (typeof render === 'function') {
        render();
    }

    ftDebugLog('campaign', { phase: 'campaign_transport_update-applied', campaignId, running, fast_forward, reason: msg?.reason });
}

window.handleCampaignTransportUpdate = handleCampaignTransportUpdate;

export { handleCampaignTransportUpdate };
